import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Form, Button, Modal, Icon, Select, Message,
} from 'semantic-ui-react';
import { isEmpty } from 'lodash';
import Validator from 'validatorjs';
import { INVALIDATE_PENDING_VRA_REASONS } from '../../constants';

const MAX_REASON_LENGTH = 255;
const OTHER_REASON_VALUE = 'Other';
const OTHER_REASON_PREFIX = 'Other: ';
const DEALER_NOTICE = 'Because this return did not meet the requirements to proceed, the DealShield '
  + 'Vehicle Return Authorization for the vehicle referenced has been cancelled.';

const InvalidatePendingForm = props => {
  const {
    isInvalidateModalOpen,
    onCloseHandler,
    onSubmitHandler,
    error,
  } = props;

  const [reasonCode, setReasonCode] = useState(null);
  const [otherReasonText, setOtherReasonText] = useState('');
  const [errors, setErrors] = useState({});

  const isOtherReason = reasonCode === OTHER_REASON_VALUE;

  const onReasonChangeHandler = (event, data) => {
    setReasonCode(data.value);
    setErrors(prevErrors => {
      const newErrors = { ...prevErrors };
      delete newErrors.reasonCode;
      return newErrors;
    });
    if (data.value !== OTHER_REASON_VALUE) {
      setOtherReasonText('');
    }
  };

  const onOtherReasonChangeHandler = (event, data) => {
    setOtherReasonText(data.value);
    setErrors(prevErrors => {
      const newErrors = { ...prevErrors };
      delete newErrors.otherReasonText;
      return newErrors;
    });
  };

  const submitForm = () => {
    const validationRules = {
      reasonCode: 'required',
      otherReasonText: `required_if:reasonCode,Other|max:${MAX_REASON_LENGTH}`,
    };
    const customMessages = {
      'required.reasonCode': 'Please select a reason.',
      'required_if.otherReasonText': 'Please input the reason.',
      'max.otherReasonText': `Reason cannot exceed ${MAX_REASON_LENGTH} characters.`,
    };
    const validation = new Validator({ reasonCode, otherReasonText: otherReasonText.trim() }, validationRules, customMessages);

    if (validation.fails()) {
      setErrors(validation.errors.all());
      return false;
    }

    setErrors({});
    onSubmitHandler({
      reason: isOtherReason ? `${OTHER_REASON_PREFIX}${otherReasonText.trim()}` : reasonCode,
    });
    return true;
  };

  return (
    <Modal
      open={isInvalidateModalOpen}
      closeIcon
      onClose={onCloseHandler}
      closeOnEscape={false}
      closeOnRootNodeClick={false}
    >
      <Modal.Header>Invalidate VRA Pending</Modal.Header>
      <Modal.Content image>
        <Modal.Description>
          {error && (
            <Message negative>
              <p>{error}</p>
            </Message>
          )}
          <Form widths='equal'>
            <Form.Field required error={!isEmpty(errors.reasonCode)}>
              <label htmlFor='invalidateReasonCode'>Reason</label>
              <Select
                id='invalidateReasonCode'
                aria-label='Select invalidation reason'
                placeholder='Select a reason'
                options={INVALIDATE_PENDING_VRA_REASONS}
                closeOnBlur
                value={reasonCode}
                onChange={onReasonChangeHandler}
              />
              {errors.reasonCode && (
                <div style={{ color: 'red' }}>{errors.reasonCode}</div>
              )}
            </Form.Field>
            {isOtherReason && (
              <Form.Field required error={!isEmpty(errors.otherReasonText)}>
                <label htmlFor='otherReasonText'>Please specify reason</label>
                <Form.TextArea
                  id='otherReasonText'
                  rows={5}
                  placeholder='Enter reason (max 255 character)'
                  value={otherReasonText}
                  onChange={onOtherReasonChangeHandler}
                />
                {errors.otherReasonText && (
                  <div style={{ color: 'red' }}>{errors.otherReasonText}</div>
                )}
              </Form.Field>
            )}
            {reasonCode && (
              <Message>
                <Message.Header>The dealer will see the following:</Message.Header>
                <p>{DEALER_NOTICE}</p>
              </Message>
            )}
          </Form>
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button color='green' onClick={submitForm} inverted>
          <Icon name='checkmark' /> Invalidate
        </Button>
        <Button onClick={onCloseHandler}>Cancel</Button>
      </Modal.Actions>
    </Modal>
  );
};

InvalidatePendingForm.propTypes = {
  isInvalidateModalOpen: PropTypes.bool.isRequired,
  onCloseHandler: PropTypes.func.isRequired,
  onSubmitHandler: PropTypes.func.isRequired,
  error: PropTypes.string,
};

InvalidatePendingForm.defaultProps = {
  error: null,
};

export default InvalidatePendingForm;
